import * as React from "react";
import { UitkCardAloha, UitkCardAlohaLink } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { UitkFigureAspectRatioType } from "uitk-react-images";
import { useLocalization } from "@shared-ui/localization-context";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { DesktopCardContent, MobileCardContent } from "./NeighborhoodCardContent";
import { NeighborhoodCardProps } from "../typings";
import { renderScrimAttributionTextHTML } from "../utils/renderScrimAttributionTextHTML";
import { NeighborhoodCardMedia } from "./NeighborhoodCardMedia";

const localizeKnownForLabel = (affinities: string) => {
  const { formatText } = useLocalization();

  return formatText("destinationNeighborhood.topNeighborhoods.knownFor", affinities);
};

const getSpecialComponent = (children: React.ReactNode) => {
  const components: { [key: string]: React.ComponentElement<any, any> | null } = {};

  React.Children.map(children, (child: React.ComponentElement<any, any>) => {
    switch (child.type.displayName) {
      case "NeighborhoodCardFooter":
        components[child.type.displayName] = child;
    }
  });

  return components;
};

export const NeighborhoodCard: React.FC<NeighborhoodCardProps> = (props) => {
  const className = props.className || "";
  const { id, link, media, region, affinities } = props.neighborhood;

  const name = region?.name || "";
  const description = region?.description || "";

  /** Concatenate affinities into a single string (e.g. beaches, surfing, seafood)  */
  const neighborhoodAffinities = affinities.reduce((result, affinity) => {
    if (result === "") {
      return affinity.name;
    }

    return `${result}, ${affinity.name}`;
  }, "");

  const averageRating = props.neighborhood.aggregateReviewsAndRatings?.averageRating;
  const totalRatings = props.neighborhood.aggregateReviewsAndRatings?.totalRatings;
  const { NeighborhoodCardFooter: CardFooter } = getSpecialComponent(props.children);
  const defaultSubTitle = neighborhoodAffinities && localizeKnownForLabel(neighborhoodAffinities);
  const containerClasses = `destinationNeighborhoodsCard uitk-cell ${className}`;

  return props.isDesktop ? (
    <div className={containerClasses}>
      <UitkCardAloha className="uitk-flex-column fullHeight" border={Boolean(props.withBorder)}>
        <div>
          <NeighborhoodCardMedia
            id={id}
            regionName={name}
            media={media}
            context={props.context}
            showFavorite={false}
            ratio={UitkFigureAspectRatioType.R21_9}
            renderAttributionText={renderScrimAttributionTextHTML}
            attributionType={props.view}
          />
        </div>
        <DesktopCardContent
          title={name}
          subTitle={props.neighborhood.infoPois}
          description={description}
          averageRating={averageRating}
          totalRatings={totalRatings}
          context={props.context}
        />
        {CardFooter}
        {props.isClickeable && (
          <UitkCardAlohaLink>
            <TrackedLink
              moduleName="DestinationNeighborhoods"
              href={new HotelSearchLinkBuilder(link).build(props.context)}
              rfrr="TG.LP.Dest.Neighborhoods"
              className="uitk-card-link"
            >
              {name}
            </TrackedLink>
          </UitkCardAlohaLink>
        )}
      </UitkCardAloha>
    </div>
  ) : (
    <div className={"destinationNeighborhoodsCard"}>
      <UitkCardAloha>
        <UitkCardAlohaLink>
          <a href={new HotelSearchLinkBuilder(link).build(props.context)}>{name}</a>
        </UitkCardAlohaLink>

        <UitkLayoutFlex>
          <UitkLayoutFlexItem minWidth="third_width" shrink={0}>
            <div>
              <NeighborhoodCardMedia
                context={props.context}
                id={id}
                regionName={name}
                media={media}
                showFavorite={props.showFavorite}
              />
            </div>
          </UitkLayoutFlexItem>

          <UitkLayoutFlexItem grow={1}>
            <div>
              <MobileCardContent title={name} subTitle={defaultSubTitle} />
            </div>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkCardAloha>
    </div>
  );
};

export const NeighborhoodCardFooter: React.FC = (props) => (
  <div className="uitk-card-content destinationNeighborhoodsCardFooter all-y-padding-three all-x-padding-three">
    {props.children}
  </div>
);
NeighborhoodCardFooter.displayName = "NeighborhoodCardFooter";

export default NeighborhoodCard;
